import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Table,
  Badge,
  Pagination,
  Tabs,
  Tab,
  Button,
  Modal,
  ListGroup,
  Row,
  Col,
  Form,
  Alert,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import api from '../api';
import axios from 'axios';
import {
  FaSyncAlt,
  FaSearch,
  FaTruck,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';

const pageStyle = {
  minHeight: '100vh',
  background:
    'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
  color: '#e5e7eb',
};

const shellStyle = {
  maxWidth: 1900,
  margin: '0 auto',
};

const cardStyle = {
  background: 'rgba(15, 23, 42, 0.86)',
  border: '1px solid rgba(148, 163, 184, 0.16)',
  borderRadius: 22,
  color: '#e5e7eb',
  boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
  backdropFilter: 'blur(10px)',
};

const cardSoftStyle = {
  background: 'rgba(30, 41, 59, 0.82)',
  border: '1px solid rgba(148, 163, 184, 0.14)',
  borderRadius: 20,
  color: '#e5e7eb',
};

const heroStyle = {
  background:
    'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(34,197,94,0.20))',
  border: '1px solid rgba(148, 163, 184, 0.18)',
  borderRadius: 28,
  color: '#f8fafc',
  boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
};

const inputStyle = {
  background: '#020617',
  color: '#e5e7eb',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  borderRadius: 12,
};

const mutedStyle = {
  color: '#94a3b8',
};

const pillStyle = {
  border: '1px solid rgba(148, 163, 184, 0.20)',
  background: 'rgba(15, 23, 42, 0.72)',
  color: '#cbd5e1',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 700,
};

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitError, setRateLimitError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('Ready to Ship');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [filters, setFilters] = useState({
    platform: '',
    customerName: '',
    startDate: '',
    endDate: '',
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [lastFetchTime, setLastFetchTime] = useState(null);

  const pollingInterval = 600000;
  const maxRetries = 3;
  const maxRetryDelay = 30000;
  const retryDelayBase = 2000;

  const statusMap = useMemo(
    () => ({
      Open: 'Open',
      'Awaiting Payment': 'Awaiting Payment',
      'Ready to Ship': 'Ready to Ship',
      Shipped: 'Shipped',
      Cancelled: 'Cancelled',
    }),
    []
  );

  const reverseStatusMap = useMemo(
    () => Object.fromEntries(Object.entries(statusMap).map(([k, v]) => [k, v])),
    [statusMap]
  );

  const tabs = useMemo(
    () => ['Open', 'Awaiting Payment', 'Ready to Ship', 'Shipped', 'Cancelled'],
    []
  );

  const platforms = useMemo(() => ['eBay', 'Shopify', 'Walmart'], []);

  const isProduction = process.env.NODE_ENV === 'production';
  const apiBaseURL = isProduction
    ? 'https://thornsdashboard.com/api'
    : 'http://localhost:3002/api';

  const isInitialLoading = loading && !firstLoadDone && orders.length === 0;
  const isRefreshing = loading && firstLoadDone;

  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');

      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${apiBaseURL}/auth/refresh`, {
        refreshToken: refreshTokenValue,
      });

      const { token } = response.data;

      localStorage.setItem('token', token);
      setIsAuthenticated(true);

      console.log(`[${new Date().toISOString()}] Token refreshed successfully`);

      return token;
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Token refresh failed:`, err.message);

      setIsAuthenticated(false);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setError('Session expired. Please log in again.');

      return null;
    }
  }, [apiBaseURL]);

  const fetchOrders = useCallback(
    async (retryCount = 0) => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        setError('');
        setRateLimitError(false);

        const params = {
          status: reverseStatusMap[activeTab] || activeTab,
          page,
          limit: 50,
          ...(filters.platform && { platform: filters.platform }),
          ...(filters.customerName && { customerName: filters.customerName }),
          ...(filters.startDate && { startDate: filters.startDate }),
          ...(filters.endDate && { endDate: filters.endDate }),
          ...(activeTab === 'Shipped' ? { shipped: 1 } : { shipped: 0 }),
        };

        console.log(`[${new Date().toISOString()}] Fetching orders with params:`, params);

        const response = await api.get('/orders', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        const fetchedOrders = response.data.data || [];

        setOrders(fetchedOrders);
        setTotalPages(response.data.meta?.totalPages || 1);
        setLastUpdated(new Date().toLocaleString());
        setLastFetchTime(Date.now());

        if (fetchedOrders.length === 0) {
          setError(`No ${activeTab.toLowerCase()} orders found.`);
        } else {
          setError('');
        }
      } catch (err) {
        console.error(
          `[${new Date().toISOString()}] Error fetching orders:`,
          err.message,
          err.response?.data
        );

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const retryAfter = err.response.headers['retry-after']
            ? parseInt(err.response.headers['retry-after'], 10) * 1000
            : retryDelayBase * Math.pow(2, retryCount) + Math.random() * 100;

          const delayMs = Math.min(retryAfter, maxRetryDelay);

          setRateLimitError(true);
          setTimeout(() => fetchOrders(retryCount + 1), delayMs);
          return;
        }

        if (err.response?.status === 401) {
          const token = await refreshToken();

          if (token && retryCount < maxRetries) {
            setTimeout(() => fetchOrders(retryCount + 1), retryDelayBase);
            return;
          }
        }

        const errorMessage =
          err.response?.status === 429
            ? 'Too many requests, please wait before trying again.'
            : err.response?.status === 401
              ? 'Session expired. Please log in again.'
              : err.response?.data?.error || 'Network error.';

        setError(`Failed to load orders: ${errorMessage}`);
        setRateLimitError(err.response?.status === 429);

        if (!firstLoadDone) {
          setOrders([]);
        }

        if (err.response?.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      } finally {
        setLoading(false);
        setFirstLoadDone(true);
      }
    },
    [
      activeTab,
      page,
      filters,
      isAuthenticated,
      reverseStatusMap,
      refreshToken,
      firstLoadDone,
    ]
  );

  const handleManualRefresh = useCallback(() => {
    console.log(`[${new Date().toISOString()}] Manual refresh triggered`);
    setError('');
    setRateLimitError(false);
    fetchOrders();
  }, [fetchOrders]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleOrderClick = useCallback(
    async (order, retryCount = 0) => {
      if (!isAuthenticated) {
        setError('Please log in to view order details.');
        return;
      }

      setSelectedOrder(order);
      setModalLoading(true);

      try {
        const productResponse = await api.get(`/products/${order.item_sku || 'unknown'}`);

        await delay(250);

        const statusHistoryResponse = await api.get(`/orders/${order.id}/status-history`);

        await delay(250);

        const pickListResponse = await api.get('/orders/picklist', {
          params: {
            order_id: order.order_id,
          },
        });

        setOrderDetails({
          product: productResponse.data?.data || null,
          statusHistory: statusHistoryResponse.data?.orders || [],
          pickList: pickListResponse.data?.orders || [],
        });
      } catch (err) {
        console.error(`[${new Date().toISOString()}] Error fetching order details:`, err.message);

        if (err.response?.status === 429 && retryCount < 1) {
          const retryAfter = err.response.headers['retry-after']
            ? parseInt(err.response.headers['retry-after'], 10) * 1000
            : retryDelayBase + Math.random() * 100;

          const delayMs = Math.min(retryAfter, maxRetryDelay);

          setRateLimitError(true);
          setTimeout(() => handleOrderClick(order, retryCount + 1), delayMs);
          return;
        }

        setOrderDetails(null);
        setError(`Failed to load order details: ${err.response?.data?.error || 'Network error'}`);
        setRateLimitError(err.response?.status === 429);

        if (err.response?.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      } finally {
        setModalLoading(false);
      }
    },
    [isAuthenticated]
  );

  const handleConfirmAction = useCallback(
    (orderId, action, message) => {
      if (!isAuthenticated) {
        setError('Please log in to perform actions.');
        return;
      }

      setConfirmAction(() => () => action(orderId));
      setConfirmMessage(message);
      setShowConfirmModal(true);
    },
    [isAuthenticated]
  );

  const executeConfirmAction = useCallback(() => {
    if (confirmAction) {
      confirmAction();
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmMessage('');
    }
  }, [confirmAction]);

  const handleCloseModal = useCallback(() => {
    setSelectedOrder(null);
    setOrderDetails(null);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmMessage('');
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const clearFilters = () => {
    setFilters({
      platform: '',
      customerName: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const handleLoginRedirect = useCallback(() => {
    window.location.href = '/login';
  }, []);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Open':
        return {
          background: 'rgba(245,158,11,0.16)',
          color: '#fcd34d',
          border: '1px solid rgba(245,158,11,0.28)',
        };
      case 'Awaiting Payment':
        return {
          background: 'rgba(14,165,233,0.16)',
          color: '#bae6fd',
          border: '1px solid rgba(14,165,233,0.28)',
        };
      case 'Ready to Ship':
        return {
          background: 'rgba(59,130,246,0.16)',
          color: '#bfdbfe',
          border: '1px solid rgba(59,130,246,0.28)',
        };
      case 'Shipped':
        return {
          background: 'rgba(34,197,94,0.16)',
          color: '#86efac',
          border: '1px solid rgba(34,197,94,0.28)',
        };
      case 'Cancelled':
        return {
          background: 'rgba(239,68,68,0.16)',
          color: '#fca5a5',
          border: '1px solid rgba(239,68,68,0.28)',
        };
      default:
        return {
          background: 'rgba(148,163,184,0.14)',
          color: '#cbd5e1',
          border: '1px solid rgba(148,163,184,0.22)',
        };
    }
  };

  const updateOrderStatus = useCallback(
    async (orderId, newStatus, newPaid, newShipped, retryCount = 0) => {
      if (!isAuthenticated) {
        setError('Please log in to update orders.');
        toast.error('Please log in to update orders.');
        return;
      }

      try {
        if (newStatus === 'Shipped') {
          const pickListResponse = await api.get('/orders/picklist', {
            params: {
              order_id: orderId,
            },
          });

          const pickList = pickListResponse.data?.orders || [];

          if (pickList.some((item) => item.status !== 'completed')) {
            toast.error('Cannot mark as Shipped: Pick list not fully completed.');
            return;
          }
        }

        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === orderId
              ? {
                  ...order,
                  status: newStatus,
                  paid: newPaid,
                  shipped: newShipped,
                }
              : order
          )
        );

        await api.put(`/orders/${orderId}`, {
          status: reverseStatusMap[newStatus] || newStatus,
          paid: newPaid,
          shipped: newShipped,
        });

        toast.success(`Order ${orderId} updated to ${newStatus}`);
        fetchOrders();
      } catch (err) {
        console.error(`[${new Date().toISOString()}] Error updating order status:`, err.message);

        fetchOrders();

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const retryAfter = err.response.headers['retry-after']
            ? parseInt(err.response.headers['retry-after'], 10) * 1000
            : retryDelayBase * Math.pow(2, retryCount) + Math.random() * 100;

          const delayMs = Math.min(retryAfter, maxRetryDelay);

          setRateLimitError(true);
          setTimeout(
            () => updateOrderStatus(orderId, newStatus, newPaid, newShipped, retryCount + 1),
            delayMs
          );
          return;
        }

        const errorMessage =
          err.response?.status === 429
            ? 'Too many requests, please wait before trying again.'
            : err.response?.status === 401
              ? 'Session expired. Please log in again.'
              : err.response?.data?.error || 'Network error.';

        setError(`Failed to update order: ${errorMessage}`);
        toast.error(`Failed to update order: ${errorMessage}`);
        setRateLimitError(err.response?.status === 429);

        if (err.response?.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
    },
    [isAuthenticated, reverseStatusMap, fetchOrders]
  );

  const cancelOrder = useCallback(
    async (orderId, retryCount = 0) => {
      if (!isAuthenticated) {
        setError('Please log in to cancel orders.');
        toast.error('Please log in to cancel orders.');
        return;
      }

      try {
        const orderResponse = await api.get(`/orders/${orderId}`);
        const { order_id, platform } = orderResponse.data;

        await api.put(`/orders/${orderId}/cancel`, {
          order_id,
          platform,
        });

        toast.success(`Order ${order_id} cancelled successfully`);
        fetchOrders();
      } catch (err) {
        console.error(`[${new Date().toISOString()}] Error cancelling order:`, err.message);

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const retryAfter = err.response.headers['retry-after']
            ? parseInt(err.response.headers['retry-after'], 10) * 1000
            : retryDelayBase * Math.pow(2, retryCount) + Math.random() * 100;

          const delayMs = Math.min(retryAfter, maxRetryDelay);

          setRateLimitError(true);
          setTimeout(() => cancelOrder(orderId, retryCount + 1), delayMs);
          return;
        }

        const errorMessage =
          err.response?.status === 429
            ? 'Too many requests, please wait before trying again.'
            : err.response?.status === 401
              ? 'Session expired. Please log in again.'
              : err.response?.data?.error || 'Network error.';

        setError(`Failed to cancel order: ${errorMessage}`);
        toast.error(`Failed to cancel order: ${errorMessage}`);
        setRateLimitError(err.response?.status === 429);

        if (err.response?.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
    },
    [isAuthenticated, fetchOrders]
  );

  const handleSyncEbayOrders = useCallback(
    async (retryCount = 0) => {
      if (!isAuthenticated) {
        setError('Please log in to sync eBay orders.');
        return;
      }

      try {
        setSyncLoading(true);
        setError('');
        setRateLimitError(false);

        await api.post('/ebay/poll-orders');

        toast.success('eBay orders synced successfully!');
        fetchOrders();
      } catch (err) {
        console.error(`[${new Date().toISOString()}] Error triggering eBay order sync:`, err.message);

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const retryAfter = err.response.headers['retry-after']
            ? parseInt(err.response.headers['retry-after'], 10) * 1000
            : retryDelayBase * Math.pow(2, retryCount) + Math.random() * 100;

          const delayMs = Math.min(retryAfter, maxRetryDelay);

          setRateLimitError(true);
          setTimeout(() => handleSyncEbayOrders(retryCount + 1), delayMs);
          return;
        }

        setError(`Failed to sync eBay orders: ${err.response?.data?.error || 'Network error'}`);
        setRateLimitError(err.response?.status === 429);
        toast.error('Failed to sync eBay orders');

        if (err.response?.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      } finally {
        setSyncLoading(false);
      }
    },
    [isAuthenticated, fetchOrders]
  );

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      setIsAuthenticated(false);
      setError('Please log in to view orders.');
      return;
    }

    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchOrders();
  }, [isAuthenticated, activeTab, page, filters, fetchOrders]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (lastFetchTime && Date.now() - lastFetchTime >= pollingInterval) {
        fetchOrders();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, lastFetchTime, pollingInterval, fetchOrders]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    return date.toString() !== 'Invalid Date' ? date.toLocaleString() : 'Invalid Date';
  }, []);

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        setPage(newPage);
      }
    },
    [totalPages]
  );

  const totalVisibleQty = useMemo(() => {
    return orders.reduce((sum, order) => {
      if (order.items?.length > 0) {
        return sum + order.items.reduce((itemSum, item) => itemSum + Number(item.quantity || 1), 0);
      }

      return sum + Number(order.item_quantity || 1);
    }, 0);
  }, [orders]);

  const platformSummary = useMemo(() => {
    return orders.reduce((acc, order) => {
      const platform = order.platform || 'Unknown';
      acc[platform] = (acc[platform] || 0) + 1;
      return acc;
    }, {});
  }, [orders]);

  const topPlatform = useMemo(() => {
    const entries = Object.entries(platformSummary).sort((a, b) => b[1] - a[1]);
    return entries[0] || null;
  }, [platformSummary]);

  const MiniMetric = ({ label, value, color = '#e5e7eb', icon }) => {
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 16,
          background: 'rgba(2, 6, 23, 0.45)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
        }}
      >
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 3 }}>
          {icon && <span className="me-1">{icon}</span>}
          {label}
        </div>
        <div style={{ color, fontWeight: 850 }}>{value}</div>
      </div>
    );
  };

  const StatCard = ({ label, value, subtext, accent = '#38bdf8', icon = '📦', valueColor }) => {
    return (
      <div className="col-md-6 col-xl-3 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
              pointerEvents: 'none',
            }}
          />

          <div className="p-3 p-lg-4 position-relative">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div
                  style={{
                    color: '#94a3b8',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  {label}
                </div>

                <div
                  className="mt-2"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    fontWeight: 900,
                    color: valueColor || '#f8fafc',
                  }}
                >
                  {value}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  background: `${accent}22`,
                  border: `1px solid ${accent}35`,
                  fontSize: 22,
                }}
              >
                {icon}
              </div>
            </div>

            {subtext && (
              <div className="mt-2" style={{ color: '#94a3b8', fontSize: 13 }}>
                {subtext}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const StatusBadge = ({ status }) => {
    const style = getStatusStyle(status);

    return (
      <Badge
        bg=""
        style={{
          ...style,
          borderRadius: 999,
          padding: '7px 10px',
        }}
      >
        {statusMap[status] || status || 'Unknown'}
      </Badge>
    );
  };

  const PlatformBadge = ({ platform }) => {
    const clean = String(platform || '').toLowerCase();

    let style = {
      background: 'rgba(148,163,184,0.14)',
      color: '#cbd5e1',
      border: '1px solid rgba(148,163,184,0.22)',
    };

    if (clean === 'ebay') {
      style = {
        background: 'rgba(59,130,246,0.16)',
        color: '#bfdbfe',
        border: '1px solid rgba(59,130,246,0.28)',
      };
    }

    if (clean === 'shopify') {
      style = {
        background: 'rgba(34,197,94,0.16)',
        color: '#86efac',
        border: '1px solid rgba(34,197,94,0.28)',
      };
    }

    if (clean === 'walmart') {
      style = {
        background: 'rgba(245,158,11,0.16)',
        color: '#fcd34d',
        border: '1px solid rgba(245,158,11,0.28)',
      };
    }

    return (
      <Badge
        bg=""
        style={{
          ...style,
          borderRadius: 999,
          padding: '7px 10px',
        }}
      >
        {platform || 'N/A'}
      </Badge>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    const start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    const adjustedStart = Math.max(1, end - maxVisible + 1);

    for (let p = adjustedStart; p <= end; p += 1) {
      items.push(
        <Pagination.Item
          key={p}
          active={page === p}
          onClick={() => handlePageChange(p)}
          disabled={rateLimitError}
        >
          {p}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
        <div style={mutedStyle}>
          Page <strong style={{ color: '#f8fafc' }}>{page}</strong> of{' '}
          <strong style={{ color: '#f8fafc' }}>{totalPages}</strong>
        </div>

        <Pagination className="orders-pagination mb-0" aria-label="Pagination">
          <Pagination.First
            disabled={page === 1 || rateLimitError}
            onClick={() => handlePageChange(1)}
          />
          <Pagination.Prev
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1 || rateLimitError}
            aria-label="Previous page"
          />
          {adjustedStart > 1 && <Pagination.Ellipsis disabled />}
          {items}
          {end < totalPages && <Pagination.Ellipsis disabled />}
          <Pagination.Next
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages || rateLimitError}
            aria-label="Next page"
          />
          <Pagination.Last
            disabled={page === totalPages || rateLimitError}
            onClick={() => handlePageChange(totalPages)}
          />
        </Pagination>
      </div>
    );
  };

  const renderOrderImages = (order) => {
    if (order.items?.length > 0) {
      return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 150 }}>
          {order.items.slice(0, 4).map((item, idx) => (
            <img
              key={idx}
              src={item.image || order.image || 'https://via.placeholder.com/50'}
              alt={`Image for ${item.title || item.sku || 'item'}`}
              className="order-thumb"
            />
          ))}

          {order.items.length > 4 && (
            <div
              className="d-flex align-items-center justify-content-center order-thumb"
              style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 900 }}
            >
              +{order.items.length - 4}
            </div>
          )}
        </div>
      );
    }

    if (order.image) {
      return (
        <img
          src={order.image}
          alt={`Product image for ${order.item_purchased || 'item'}`}
          className="order-thumb"
        />
      );
    }

    return <span style={mutedStyle}>N/A</span>;
  };

  const renderOrderActions = (order) => {
    return (
      <div className="d-flex flex-wrap gap-2">
        {['Open', 'Awaiting Payment', 'Ready to Ship'].includes(activeTab) && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleConfirmAction(
                order.id,
                cancelOrder,
                `Are you sure you want to cancel order ${order.order_id}?`
              );
            }}
            aria-label={`Cancel order ${order.order_id}`}
            disabled={rateLimitError}
          >
            Cancel
          </Button>
        )}

        {activeTab === 'Open' && (
          <>
            <Button
              variant="outline-info"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmAction(
                  order.id,
                  () => updateOrderStatus(order.id, 'Awaiting Payment', false, order.shipped),
                  `Mark order ${order.order_id} as Awaiting Payment?`
                );
              }}
              disabled={rateLimitError}
            >
              Awaiting
            </Button>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmAction(
                  order.id,
                  () => updateOrderStatus(order.id, 'Ready to Ship', true, order.shipped),
                  `Mark order ${order.order_id} as Ready to Ship? This will mark it as paid.`
                );
              }}
              disabled={rateLimitError}
            >
              Ready
            </Button>
          </>
        )}

        {activeTab === 'Awaiting Payment' && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleConfirmAction(
                order.id,
                () => updateOrderStatus(order.id, 'Ready to Ship', true, order.shipped),
                `Mark order ${order.order_id} as Paid and Ready to Ship?`
              );
            }}
            disabled={rateLimitError}
          >
            Paid & Ready
          </Button>
        )}

        {activeTab === 'Ready to Ship' && (
          <Button
            variant="outline-success"
            size="sm"
            disabled={rateLimitError}
            onClick={(e) => {
              e.stopPropagation();
              handleConfirmAction(
                order.id,
                () => updateOrderStatus(order.id, 'Shipped', true, true),
                `Mark order ${order.order_id} as Shipped?`
              );
            }}
            title="Backend will verify pick list completion before shipping."
          >
            Shipped
          </Button>
        )}
      </div>
    );
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 8 }).map((_, index) => (
      <tr key={`skeleton-${index}`}>
        {Array.from({ length: 11 }).map((__, cellIndex) => (
          <td key={cellIndex}>
            <div className="skeleton-line" />
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          .orders-dark input,
          .orders-dark select,
          .orders-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .orders-dark input::placeholder,
          .orders-dark textarea::placeholder {
            color: #64748b !important;
          }

          .orders-dark input:focus,
          .orders-dark select:focus,
          .orders-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.18) !important;
            border-color: rgba(56, 189, 248, 0.75) !important;
          }

          .orders-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .orders-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .orders-dark .nav-tabs {
            border-bottom: 1px solid rgba(148, 163, 184, 0.16);
          }

          .orders-dark .nav-tabs .nav-link {
            color: #94a3b8;
            background: rgba(15, 23, 42, 0.58);
            border: 1px solid rgba(148, 163, 184, 0.12);
            border-radius: 14px 14px 0 0;
            margin-right: 6px;
            font-weight: 800;
          }

          .orders-dark .nav-tabs .nav-link.active {
            color: #f8fafc;
            background: rgba(37,99,235,0.24);
            border-color: rgba(59,130,246,0.45);
          }

          .orders-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
            margin-bottom: 0;
          }

          .orders-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            white-space: nowrap;
            vertical-align: middle;
          }

          .orders-dark .table tbody td {
            vertical-align: middle;
            border-color: rgba(148, 163, 184, 0.10);
            white-space: nowrap;
          }

          .orders-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48);
            cursor: pointer;
          }

          .orders-dark .pagination .page-link {
            background: rgba(15, 23, 42, 0.88);
            border-color: rgba(148, 163, 184, 0.18);
            color: #cbd5e1;
            font-weight: 700;
          }

          .orders-dark .pagination .page-item.active .page-link {
            background: #2563eb;
            border-color: #2563eb;
            color: #ffffff;
          }

          .orders-dark .pagination .page-item.disabled .page-link {
            background: rgba(15, 23, 42, 0.55);
            border-color: rgba(148, 163, 184, 0.10);
            color: #64748b;
          }

          .orders-modal .modal-content {
            background: #0f172a;
            border: 1px solid rgba(148, 163, 184, 0.18);
            border-radius: 22px;
            color: #e5e7eb;
            box-shadow: 0 24px 70px rgba(0,0,0,0.45);
          }

          .orders-modal .modal-header {
            border-bottom: 1px solid rgba(148, 163, 184, 0.14);
          }

          .orders-modal .modal-footer {
            border-top: 1px solid rgba(148, 163, 184, 0.14);
          }

          .orders-modal .btn-close {
            filter: invert(1) grayscale(100%) brightness(200%);
          }

          .orders-modal .list-group-item {
            background: rgba(2, 6, 23, 0.45);
            border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
          }

          .order-thumb {
            width: 54px;
            height: 54px;
            object-fit: contain;
            border-radius: 14px;
            border: 1px solid rgba(148, 163, 184, 0.22);
            background: rgba(2, 6, 23, 0.60);
            padding: 4px;
          }

          .orders-status-icon.paid,
          .orders-status-icon.shipped {
            color: #86efac;
            font-size: 18px;
          }

          .orders-status-icon.not-paid,
          .orders-status-icon.not-shipped {
            color: #fca5a5;
            font-size: 18px;
          }

          .skeleton-line {
            height: 18px;
            width: 100%;
            border-radius: 999px;
            background: linear-gradient(
              90deg,
              rgba(148, 163, 184, 0.10),
              rgba(148, 163, 184, 0.22),
              rgba(148, 163, 184, 0.10)
            );
            background-size: 200% 100%;
            animation: skeletonPulse 1.15s ease-in-out infinite;
          }

          @keyframes skeletonPulse {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }

          .refresh-overlay {
            position: absolute;
            top: 16px;
            right: 16px;
            z-index: 5;
            border-radius: 999px;
            padding: 8px 12px;
            background: rgba(2, 6, 23, 0.88);
            border: 1px solid rgba(59, 130, 246, 0.34);
            color: #bfdbfe;
            font-size: 12px;
            font-weight: 900;
            box-shadow: 0 12px 30px rgba(0,0,0,0.30);
          }

          .orders-table-wrap {
            position: relative;
          }

          .orders-table-wrap.refreshing {
            opacity: 0.88;
          }
        `}
      </style>

      <Container fluid className="py-4 orders-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Orders
                </span>
                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  {activeTab}
                </span>
                <span style={{ ...pillStyle, color: '#fef3c7', borderColor: 'rgba(245,158,11,0.38)' }}>
                  Smooth Loading
                </span>
                <span style={{ ...pillStyle, color: '#ddd6fe', borderColor: 'rgba(168,85,247,0.38)' }}>
                  Auto Polling
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Orders Control Center
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 940, fontSize: 17 }}>
                Review orders by status, sync eBay orders, filter by customer or platform,
                open order details, and manage fulfillment actions without the page flashing empty.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 330,
              }}
            >
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Current View
                  </div>

                  <div style={{ color: '#f8fafc', fontSize: 26, fontWeight: 900 }}>
                    Page {page} / {totalPages}
                  </div>
                </div>

                <Button
                  variant="outline-info"
                  onClick={handleManualRefresh}
                  disabled={loading || !isAuthenticated || rateLimitError}
                >
                  {loading ? <Spinner size="sm" /> : <FaSyncAlt />}
                </Button>
              </div>

              <Row className="g-2">
                <Col xs={6}>
                  <MiniMetric label="Rows" value={orders.length.toLocaleString()} color="#38bdf8" />
                </Col>
                <Col xs={6}>
                  <MiniMetric label="Items Qty" value={totalVisibleQty.toLocaleString()} color="#c4b5fd" />
                </Col>
              </Row>

              {lastUpdated && (
                <div className="mt-3" style={{ color: '#94a3b8', fontSize: 13 }}>
                  Last updated: {lastUpdated}
                </div>
              )}
            </div>
          </div>
        </div>

        {!isAuthenticated && (
          <Alert
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
              borderRadius: 18,
            }}
          >
            {error || 'Please log in to view orders.'}{' '}
            <Button variant="light" size="sm" onClick={handleLoginRedirect}>
              Log In
            </Button>
          </Alert>
        )}

        {isAuthenticated && (
          <>
            {error && !error.startsWith('No ') && (
              <Alert
                style={{
                  background: rateLimitError
                    ? 'rgba(120,53,15,0.75)'
                    : 'rgba(127,29,29,0.70)',
                  border: rateLimitError
                    ? '1px solid rgba(251,191,36,0.35)'
                    : '1px solid rgba(248,113,113,0.35)',
                  color: rateLimitError ? '#fef3c7' : '#fee2e2',
                  borderRadius: 18,
                }}
              >
                <strong>{rateLimitError ? 'Rate limit detected.' : 'Orders notice.'}</strong>
                <div>
                  {error} {rateLimitError && 'Please wait a moment before retrying.'}
                </div>

                <Button
                  variant="light"
                  size="sm"
                  className="mt-2"
                  onClick={handleManualRefresh}
                  disabled={rateLimitError}
                >
                  Retry
                </Button>
              </Alert>
            )}

            <Row className="mb-4">
              <StatCard
                label="Current Tab Rows"
                value={orders.length.toLocaleString()}
                subtext={`Showing ${activeTab}`}
                accent="#38bdf8"
                icon="📋"
              />

              <StatCard
                label="Total Item Qty"
                value={totalVisibleQty.toLocaleString()}
                subtext="Quantity across visible orders"
                accent="#22c55e"
                icon="📦"
                valueColor="#86efac"
              />

              <StatCard
                label="Top Platform"
                value={topPlatform ? topPlatform[0] : 'None'}
                subtext={topPlatform ? `${topPlatform[1]} visible order(s)` : 'No orders loaded'}
                accent="#a855f7"
                icon="🛒"
                valueColor="#c4b5fd"
              />

              <StatCard
                label="Sync Status"
                value={syncLoading ? 'Syncing' : isRefreshing ? 'Refreshing' : 'Ready'}
                subtext="Manual eBay order sync"
                accent="#f59e0b"
                icon="🔄"
                valueColor={syncLoading || isRefreshing ? '#fbbf24' : '#f8fafc'}
              />
            </Row>

            <div className="p-4 mb-4" style={cardStyle}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                    Actions
                  </h4>
                  <p className="mb-0" style={mutedStyle}>
                    Refresh orders, sync eBay, and filter the order table.
                  </p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    variant="outline-info"
                    onClick={handleManualRefresh}
                    disabled={loading || !isAuthenticated || rateLimitError}
                  >
                    <FaSyncAlt className="me-2" />
                    {isRefreshing ? 'Refreshing...' : 'Refresh Orders'}
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleSyncEbayOrders}
                    disabled={syncLoading || !isAuthenticated || rateLimitError}
                  >
                    {syncLoading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <FaExternalLinkAlt className="me-2" />
                        Sync eBay Orders
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Row className="g-3 align-items-end">
                <Col xl={3} md={6}>
                  <Form.Group>
                    <Form.Label>Platform</Form.Label>
                    <Form.Select
                      name="platform"
                      value={filters.platform || ''}
                      onChange={handleFilterChange}
                      disabled={rateLimitError}
                      style={inputStyle}
                    >
                      <option value="">All Platforms</option>
                      {platforms.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xl={3} md={6}>
                  <Form.Group>
                    <Form.Label>Customer Name</Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          background: '#020617',
                          border: '1px solid rgba(148, 163, 184, 0.35)',
                          color: '#94a3b8',
                          borderRadius: '12px 0 0 12px',
                        }}
                      >
                        <FaSearch />
                      </InputGroup.Text>

                      <Form.Control
                        type="text"
                        name="customerName"
                        value={filters.customerName || ''}
                        onChange={handleFilterChange}
                        placeholder="Search customer"
                        disabled={rateLimitError}
                        style={{ ...inputStyle, borderRadius: '0 12px 12px 0' }}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col xl={2} md={6}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={filters.startDate || ''}
                      onChange={handleFilterChange}
                      disabled={rateLimitError}
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col xl={2} md={6}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={filters.endDate || ''}
                      onChange={handleFilterChange}
                      disabled={rateLimitError}
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                <Col xl={2} md={12}>
                  <Button variant="outline-light" className="w-100" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="p-4" style={cardStyle}>
              <Tabs
                activeKey={activeTab}
                onSelect={(key) => {
                  setActiveTab(key);
                  setPage(1);
                  setError('');
                  setRateLimitError(false);
                }}
                className="orders-tabs mb-3"
              >
                {tabs.map((tab) => (
                  <Tab
                    eventKey={tab}
                    title={`${tab} (${tab === activeTab ? orders.length : '-'})`}
                    key={tab}
                  >
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                      <div>
                        <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                          {tab} Orders
                        </h4>
                        <p className="mb-0" style={mutedStyle}>
                          Orders count: {orders.length.toLocaleString()}
                        </p>
                      </div>

                      <span style={{ ...pillStyle, color: isRefreshing ? '#fbbf24' : '#bfdbfe' }}>
                        {isRefreshing ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Refreshing
                          </>
                        ) : (
                          `Page ${page} of ${totalPages}`
                        )}
                      </span>
                    </div>

                    <div className={`orders-table-wrap ${isRefreshing ? 'refreshing' : ''}`}>
                      {isRefreshing && (
                        <div className="refresh-overlay">
                          <Spinner size="sm" className="me-2" />
                          Updating orders...
                        </div>
                      )}

                      <div className="table-responsive">
                        <Table hover responsive className="align-middle" role="grid" aria-label="Orders table">
                          <thead>
                            <tr>
                              <th>Image</th>
                              <th>Order #</th>
                              <th>Order Date</th>
                              <th>Status</th>
                              <th>Paid</th>
                              <th>Shipped</th>
                              <th>Channel</th>
                              <th>SKU</th>
                              <th className="text-end">Qty</th>
                              <th>Buyer</th>
                              <th>Actions</th>
                            </tr>
                          </thead>

                          <tbody>
                            {isInitialLoading ? (
                              renderSkeletonRows()
                            ) : orders.length === 0 ? (
                              <tr>
                                <td colSpan="11" className="text-center py-5">
                                  <div style={{ color: '#f8fafc', fontWeight: 900, fontSize: 18 }}>
                                    {error || 'No orders found'}
                                  </div>
                                  <div style={mutedStyle}>
                                    Try changing filters or refreshing orders.
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              orders.map((order) => (
                                <tr
                                  key={`${order.platform}-${order.order_id}-${order.id}`}
                                  onClick={() => !rateLimitError && handleOrderClick(order)}
                                  role="row"
                                  aria-label={`Order ${order.order_id}`}
                                >
                                  <td>{renderOrderImages(order)}</td>

                                  <td>
                                    <strong style={{ color: '#f8fafc' }}>
                                      {order.order_id || 'N/A'}
                                    </strong>
                                  </td>

                                  <td>{formatDate(order.order_date)}</td>

                                  <td>
                                    <StatusBadge status={order.status} />
                                  </td>

                                  <td>
                                    {order.paid ? (
                                      <span className="orders-status-icon paid" aria-label="Paid">
                                        ●
                                      </span>
                                    ) : (
                                      <span className="orders-status-icon not-paid" aria-label="Not Paid">
                                        ○
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    {order.shipped ? (
                                      <span className="orders-status-icon shipped" aria-label="Shipped">
                                        ●
                                      </span>
                                    ) : (
                                      <span className="orders-status-icon not-shipped" aria-label="Not Shipped">
                                        ○
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    <PlatformBadge platform={order.platform} />
                                  </td>

                                  <td style={{ minWidth: 190, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {order.items?.length > 0 ? (
                                      <div>
                                        {order.items.map((item, idx) => (
                                          <div key={idx} style={{ marginBottom: 2 }}>
                                            <strong style={{ color: '#f8fafc' }}>{item.sku || 'N/A'}</strong>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <strong style={{ color: '#f8fafc' }}>
                                        {order.item_sku || 'N/A'}
                                      </strong>
                                    )}
                                  </td>

                                  <td className="text-end">
                                    {order.items?.length > 0 ? (
                                      <div>
                                        {order.items.map((item, idx) => (
                                          <div key={idx}>{item.quantity || 1}</div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span>{order.item_quantity || 1}</span>
                                    )}
                                  </td>

                                  <td>{order.customer_name || 'Unknown'}</td>

                                  <td onClick={(e) => e.stopPropagation()}>
                                    {renderOrderActions(order)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>

                    {!isInitialLoading && renderPagination()}
                  </Tab>
                ))}
              </Tabs>
            </div>
          </>
        )}

        <Modal
          show={!!selectedOrder}
          onHide={handleCloseModal}
          size="xl"
          aria-label="order-details-modal"
          centered
          className="orders-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title id="order-details-modal">
              Order Details: {selectedOrder?.order_id}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {modalLoading && (
              <div className="text-center p-5">
                <Spinner animation="border" variant="info" role="status">
                  <span className="visually-hidden">Loading details...</span>
                </Spinner>
                <div className="mt-3" style={mutedStyle}>
                  Loading order details...
                </div>
              </div>
            )}

            {!modalLoading && selectedOrder && (
              <Row className="g-4">
                <Col lg={6}>
                  <div className="p-3 mb-3" style={cardSoftStyle}>
                    <h5 style={{ color: '#f8fafc', fontWeight: 900 }}>Order Information</h5>

                    <Row className="g-2 mt-2">
                      <Col md={6}>
                        <MiniMetric label="Platform" value={selectedOrder.platform || 'N/A'} color="#bfdbfe" />
                      </Col>
                      <Col md={6}>
                        <MiniMetric label="Status" value={statusMap[selectedOrder.status] || selectedOrder.status || 'Unknown'} color="#86efac" />
                      </Col>
                      <Col md={6}>
                        <MiniMetric label="Order Date" value={formatDate(selectedOrder.order_date)} color="#c4b5fd" />
                      </Col>
                      <Col md={6}>
                        <MiniMetric label="Buyer" value={selectedOrder.customer_name || 'Unknown'} color="#fbbf24" />
                      </Col>
                    </Row>
                  </div>

                  <div className="p-3 mb-3" style={cardSoftStyle}>
                    <h5 style={{ color: '#f8fafc', fontWeight: 900 }}>Item Information</h5>

                    {selectedOrder.items?.length > 0 ? (
                      <ListGroup variant="flush">
                        {selectedOrder.items.map((item, idx) => (
                          <ListGroup.Item key={idx}>
                            <strong>SKU:</strong> {item.sku || 'N/A'}
                            <br />
                            <strong>Title:</strong> {item.title || 'N/A'}
                            <br />
                            <strong>Quantity:</strong> {item.quantity || 'N/A'}
                            <br />
                            <strong>Price:</strong> ${item.price || 'N/A'}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <ListGroup variant="flush">
                        <ListGroup.Item>
                          <strong>SKU:</strong> {selectedOrder.item_sku || 'N/A'}
                          <br />
                          <strong>Title:</strong> {selectedOrder.item_purchased || 'N/A'}
                          <br />
                          <strong>Quantity:</strong> 1
                        </ListGroup.Item>
                      </ListGroup>
                    )}
                  </div>

                  <div className="p-3 mb-3" style={cardSoftStyle}>
                    <h5 style={{ color: '#f8fafc', fontWeight: 900 }}>Pick List Status</h5>

                    {orderDetails?.pickList?.length ? (
                      <ListGroup variant="flush">
                        {orderDetails.pickList.map((item, idx) => (
                          <ListGroup.Item key={idx}>
                            <strong>SKU:</strong> {item.sku || 'N/A'}
                            <br />
                            <strong>Status:</strong> {item.status || 'N/A'}
                            <br />
                            <strong>Quantity Picked:</strong> {item.quantity_picked || '0'}/
                            {item.quantity_ordered || '0'}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <p style={mutedStyle} className="mb-0">
                        No pick list available.
                      </p>
                    )}
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="p-3 mb-3" style={cardSoftStyle}>
                    <h5 style={{ color: '#f8fafc', fontWeight: 900 }}>Tracking Information</h5>

                    {selectedOrder.tracking_number ? (
                      <Row className="g-2">
                        <Col md={7}>
                          <MiniMetric label="Tracking Number" value={selectedOrder.tracking_number} color="#38bdf8" />
                        </Col>
                        <Col md={5}>
                          <MiniMetric label="Carrier" value={selectedOrder.carrier || 'N/A'} color="#c4b5fd" />
                        </Col>
                      </Row>
                    ) : (
                      <p style={mutedStyle} className="mb-0">
                        No tracking information available.
                      </p>
                    )}
                  </div>

                  <div className="p-3 mb-3" style={cardSoftStyle}>
                    <h5 style={{ color: '#f8fafc', fontWeight: 900 }}>Status History</h5>

                    {orderDetails?.statusHistory?.length > 0 ? (
                      <ListGroup variant="flush">
                        {orderDetails.statusHistory.map((entry, index) => (
                          <ListGroup.Item key={index}>
                            <strong>{statusMap[entry.status] || entry.status}</strong> -{' '}
                            {formatDate(entry.timestamp) || 'N/A'} by {entry.user || 'system'}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <p style={mutedStyle} className="mb-0">
                        No status history available.
                      </p>
                    )}
                  </div>

                  <div className="p-3" style={cardSoftStyle}>
                    <h5 style={{ color: '#f8fafc', fontWeight: 900 }}>Tracking Map</h5>
                    <p style={mutedStyle}>Placeholder: Tracking map integration pending.</p>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        height: 180,
                        borderRadius: 18,
                        background: 'rgba(2, 6, 23, 0.45)',
                        border: '1px solid rgba(148, 163, 184, 0.12)',
                        color: '#94a3b8',
                        fontWeight: 900,
                      }}
                    >
                      <FaTruck className="me-2" />
                      Tracking Map
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Modal.Body>

          <Modal.Footer>
            {selectedOrder &&
              ['Open', 'Awaiting Payment', 'Ready to Ship'].includes(
                statusMap[selectedOrder.status] || selectedOrder.status
              ) && (
                <Button
                  variant="outline-danger"
                  onClick={() =>
                    handleConfirmAction(
                      selectedOrder.id,
                      cancelOrder,
                      `Are you sure you want to cancel order ${selectedOrder.order_id}?`
                    )
                  }
                  disabled={rateLimitError}
                >
                  Cancel Order
                </Button>
              )}

            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showConfirmModal}
          onHide={handleCloseConfirmModal}
          centered
          className="orders-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Action</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div
              className="p-3"
              style={{
                borderRadius: 18,
                background: 'rgba(245,158,11,0.10)',
                border: '1px solid rgba(245,158,11,0.20)',
                color: '#fef3c7',
              }}
            >
              {confirmMessage}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseConfirmModal}>
              Cancel
            </Button>

            <Button variant="primary" onClick={executeConfirmAction} disabled={rateLimitError}>
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>

        <ToastContainer position="top-right" autoClose={3000} />
      </Container>
    </div>
  );
}

export default Orders;